import { Link } from '@tanstack/react-router';

import { ProgressBar } from '../ui/progress-bar';

import { useLearnerPosition } from './use-learner-position';

/**
 * Orients the learner at a glance: module, progress, current arc.
 * The expanded sidebar provides progress after the primary navigation.
 */
export function SidebarPosition({ collapsed }: { collapsed: boolean }) {
  const { data, isPending } = useLearnerPosition();

  // The compact rail already links to Studio; do not repeat it as an unexplained badge.
  if (collapsed) return null;

  if (isPending) {
    return (
      <div className="mx-3 mb-6 mt-7 animate-pulse space-y-2.5" aria-hidden="true">
        <div className="bg-surface h-3 w-24 rounded" />
        <div className="bg-surface h-1.5 w-full" />
        <div className="bg-surface h-3 w-32 rounded" />
      </div>
    );
  }

  if (!data)
    return (
      <Link to="/studio" className="text-accent mx-3 mb-6 mt-7 text-sm hover:underline">
        Browse curriculum
      </Link>
    );

  const percent = data.completionPercent;

  return (
    <Link
      to="/studio"
      className="focus-visible:ring-ring border-line group mx-3 mb-6 mt-7 block border-t pt-5 outline-none focus-visible:ring-2"
    >
      <p className="text-fg-muted mb-2 text-xs">Your progress</p>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold tabular-nums">
          Module {data.currentModule}
          <span className="text-fg-muted font-normal"> of {data.totalModules}</span>
        </span>
        <span className="text-fg-muted text-xs tabular-nums">{Math.round(percent)}%</span>
      </div>
      <ProgressBar value={percent} label="Residency progress" className="mt-2" />
      <p className="text-fg-muted group-hover:text-fg mt-2 text-sm leading-5 transition-colors [overflow-wrap:anywhere]">
        {data.module.title}
      </p>
    </Link>
  );
}
